import { Button, IconButton, InputBar } from '~/ui';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { Modal } from '~/components/Shared/Modal/Modal';
import { useTranslation } from 'react-i18next';
import PhotoIcon from './images/image-icon.svg?react';
import { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { IconsContainer } from '../CreateCategory/IconsContainer/IconsContainer';
import { validate } from '~/utils/validateKnowledge';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { ICreateProps, IFormik } from '../CreateCategory/CreateCategory';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { SvgIcon } from '~/ui/SvgIcon/SvgIcon';
import { svgIcons } from '~/utils/getIcons';
import ReactDOMServer from 'react-dom/server';
import DeleteSvg from '~/assets/svg/newSvg/delete.svg?react';
import styles from './EditCategory.module.scss';
import CustomQuerySelect from '~/components/Shared/CustomQuerySelect/CustomQuerySelect';

const EditCategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { categoryId } = useParams();
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const client = useQueryClient();

  const { mutateAsync: editCategory } = useMutation(
    async ({
      name,
      assets,
      icon,
      organizations,
      accommodation_ids,
      organization_all,
      accommodation_all,
    }: ICreateProps) => {
      const response = await instance.put(
        `admin/knowledge-base-categories/${categoryId}`,
        {
          name,
          asset_id: assets.id,
          svg_icon: icon,
          ...(organization_all ? {} : { organization_ids: organizations }),
          ...(accommodation_all ? {} : { accommodation_ids }),
          ...(accommodation_all ? { accommodation_all } : {}),
          ...(organization_all ? { organization_all } : {}),
        }
      );

      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['knowledge-data', categoryId]);
        navigate('/knowledge-base');
        toastSuccess(t('new_accommodation_added'));
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data: knowledgeData, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(
        `admin/knowledge-base-categories/${categoryId}`
      );
      return data;
    },
    queryKey: ['knowledge-data', categoryId],
    onError: error => {
      toastError(error);
    },
  });

  const { data: assetsArray, isLoading: isAssetsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets');
      return data.data;
    },
    queryKey: ['assets'],
    onError: error => {
      toastError(error);
    },
  });

  const formik = useFormik<IFormik>({
    initialValues: {
      name: '',
      assets: {
        id: 0,
        name: '',
      },
      accommodations: [],
      organizations: [],
      iconId: null,
      icon: null,
      accommodation_all: false,
      organization_all: false,
    },
    validate,
    onSubmit: values => {
      const stringIcon =
        values.icon !== null
          ? ReactDOMServer.renderToStaticMarkup(values.icon)
          : '';
      const accommodation_ids = values.accommodations.map(item => item.id);
      const organizations = values.organizations.map(item => item.id);
      const data = {
        name: values.name,
        assets: values.assets,
        accommodation_ids,
        organizations,
        icon: stringIcon,
        accommodation_all: values.accommodation_all,
        organization_all: values.organization_all,
      };
      editCategory(data);
    },
  });

  const setValues = formik.setValues;

  useEffect(() => {
    formik.setFieldValue('organizations', []);
  }, [formik.values.accommodations]);

  useEffect(() => {
    if (formik.values.iconId === null) return;
    const Icon = svgIcons[formik.values.iconId].icon;
    formik.setFieldValue('icon', <Icon className={styles.icon} />);
  }, [formik.values.iconId]);

  useEffect(() => {
    if (!knowledgeData && isLoading && !assetsArray && isAssetsLoading) return;

    const icon = (
      <SvgIcon
        svgString={knowledgeData?.svg_icon}
        className={styles.comingIcon}
      />
    );

    const currentAsset =
      assetsArray?.filter(
        (item: { id: number }) => item.id === knowledgeData?.asset_id
      ) || [];

    setValues({
      name: knowledgeData?.name,
      assets: {
        id: isSuperAdmin ? currentAsset[0]?.id : assetsArray[0]?.id,
        name: isSuperAdmin ? currentAsset[0]?.name : assetsArray[0]?.name,
      },
      accommodations: knowledgeData?.accommodations,
      organizations: knowledgeData?.organizations,
      iconId: null,
      icon: icon,
      accommodation_all: false,
      organization_all: false,
    });
  }, [categoryId, setValues, knowledgeData, assetsArray]);

  return (
    <Page
      heading={
        <Heading
          text={t('edit_category')}
          classNameText={styles.createCategoryHeading}
        />
      }
    >
      <form className={styles.createCategory} onSubmit={formik.handleSubmit}>
        <div className={styles.selectWrapper}>
          <InputBar
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            classNameInput={styles.input}
            label={t('category_name')}
            placeholder={t('category_name')}
            star
            errors={formik.errors.name}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
          />
          <CustomSelect
            placeholder={t('to_choose')}
            onChange={value => {
              formik.setFieldValue('assets', value);
            }}
            label={t('asset')}
            value={formik.values.assets}
            className={styles.select}
            disabled={!isSuperAdmin || isAssetsLoading}
            options={assetsArray}
            error={formik.errors.assets ? formik.errors.assets.name : undefined}
            onTouch={() => formik.setFieldTouched('assets', true)}
            required
          />
        </div>

        <div className={styles.selectWrapper}>
          <div className={styles.querySelectWrapper}>
            <CustomQuerySelect
              label={t('accommodation_facility')}
              className={styles.querySelect}
              onChange={value => {
                formik.setFieldValue('accommodations', value);
              }}
              value={formik.values.accommodations}
              query="admin/accommodations"
              queryKey={[
                'accommodations',
                {
                  sort: '',
                  perPage: 100,
                  field: '',
                  page: 1,
                  asset_id: formik.values.assets.id,
                },
              ]}
              params={{
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
                asset_id: formik.values.assets.id,
              }}
              required
              placeholder={t('to_choose')}
              multiselect
              isSearchEnabled
              onTouch={() => formik.setFieldTouched('accommodations', true)}
              error={
                formik.touched.accommodations
                  ? (formik.errors.accommodations as string)
                  : ''
              }
              disabled={
                (formik.values.assets.id === 0 && !formik.values.assets.name) ||
                formik.values.accommodation_all
              }
              hasCheckbox={
                formik.values.assets.id !== 0 && !!formik.values.assets.name
              }
              checkboxLabel={t('all_accommodations')}
              setIsCheckedAll={value =>
                formik.setFieldValue('accommodation_all', value)
              }
            />
          </div>

          <div className={styles.querySelectWrapper}>
            <CustomQuerySelect
              label={t('organizations')}
              query="admin/organization"
              onChange={value => {
                formik.setFieldValue('organizations', value);
              }}
              placeholder={t('validate_organizations')}
              value={formik.values.organizations}
              className={styles.querySelect}
              isSearchEnabled
              queryKey={[
                'organizations',
                {
                  sort: '',
                  perPage: 100,
                  field: '',
                  page: 1,
                  accommodation_ids: formik.values.accommodations.map(i => i.id),
                },
              ]}
              params={{
                sort: '',
                perPage: 100,
                field: '',
                page: 1,
                accommodation_ids: formik.values.accommodations.map(i => i.id),
              }}
              required
              multiselect
              error={formik.errors.organizations as string}
              disabled={formik.values.organization_all}
              hasCheckbox
              checkboxLabel={t('all_organizations')}
              setIsCheckedAll={value =>
                formik.setFieldValue('organization_all', value)
              }
            />
          </div>
        </div>

        {formik.values.icon !== null ? (
          <div className={styles.iconWrapper}>
            {formik.values.icon}
            <button
              type="button"
              className={styles.deleteBtd}
              onClick={() => {
                formik.setFieldValue('iconId', null);
                formik.setFieldValue('icon', null);
              }}
            >
              <DeleteSvg />
            </button>
          </div>
        ) : (
          <div className={styles.iconBtnWrapper}>
            <IconButton
              onClick={() => setIsModalOpen(true)}
              className={styles.photoPickerButton}
            >
              <PhotoIcon />
              <span className={styles.photoPickerText}>
                {t('choose_category_icon')}
              </span>
            </IconButton>
            {formik.errors.icon && (
              <span className={styles.iconBtnError}>{t('select_icon')}</span>
            )}
          </div>
        )}
        <Button
          type="submit"
          text={t('save_n_exit')}
          className={styles.submitButton}
          disabled={!(formik.isValid && formik.dirty)}
        />
      </form>

      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <IconsContainer
          onCloseClick={() => setIsModalOpen(false)}
          setSelectedId={value => formik.setFieldValue('iconId', value)}
        />
      </Modal>
    </Page>
  );
};

export default EditCategory;
